/** Cloze (fill-in-the-blank) step — inline blanks within text, filled by tapping word chips. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { RotateCcw, Delete } from 'lucide-react';
import DiagramRenderer from '@/shared/components/DiagramRenderer';
import {
  PromptRenderer,
  extractBlankIds,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { useSession } from '../SessionContext';
import { AnimatedKbd, useShortcutOverlay } from '../components/ShortcutOverlay';
import { useKeyboardRipple, RippleFlash } from '../components/KeyboardRipple';
import { StepShell } from './StepShell';
import { FeedbackDisplay } from './FeedbackDisplay';
import type { ClozeConfig } from '../types';

interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

// ─── Types ──────────────────────────────────────────────────────────────────

type ClozeBlank = ClozeConfig['blanks'][number];

// ─── Constants ──────────────────────────────────────────────────────────────

const EMPTY_BLANKS: ClozeBlank[] = [];
const EMPTY_OPTIONS: string[] = [];
const EMPTY_BLANKS_MAP: Record<string, string> = {};

// ─── Graded blank (shared by FT and MC) ─────────────────────────────────────

function GradedBlank({
  value,
  blankResult,
}: {
  value: string;
  blankResult: { isCorrect: boolean; correctAnswer: string };
}) {
  const isCorrect = blankResult.isCorrect;
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '0 var(--f-space-1)',
        verticalAlign: 'baseline',
        lineHeight: 'var(--f-leading-snug)',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          padding: '2px var(--f-space-3)',
          borderRadius: 'var(--f-radius-xl)',
          border: `2px solid ${isCorrect ? 'var(--f-success)' : 'var(--f-error)'}`,
          backgroundColor: isCorrect ? 'var(--f-success-subtle)' : 'var(--f-error-subtle)',
          color: isCorrect ? 'var(--f-success-text)' : 'var(--f-error-text)',
          fontWeight: 500,
          fontSize: 'inherit',
          textDecoration: isCorrect ? 'none' : 'line-through',
        }}
      >
        {value || '\u2014'}
      </span>
      {!isCorrect && (
        <span
          style={{
            fontSize: 'var(--f-text-sm)',
            color: 'var(--f-success-text)',
            fontWeight: 500,
            marginTop: '2px',
          }}
        >
          {blankResult.correctAnswer}
        </span>
      )}
    </span>
  );
}

// ─── Free-text blank ────────────────────────────────────────────────────────

function FreeTextBlank({
  blank,
  value,
  isDisabled,
  onChange,
  autoFocus,
}: {
  blank: ClozeBlank;
  value: string;
  isDisabled: boolean;
  onChange: (blankId: string, value: string) => void;
  autoFocus?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      disabled={isDisabled}
      onChange={(e) => onChange(blank.id, e.target.value)}
      placeholder="..."
      className="inline-block mx-1 px-3 py-1 rounded-xl border-2 align-baseline transition-all duration-200 outline-none focus:ring-2 focus:ring-offset-1"
      style={{
        borderColor: 'var(--f-border-strong)',
        backgroundColor: 'var(--f-bg-raised)',
        color: 'var(--f-fg)',
        fontFamily: 'var(--f-font-sans)',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'var(--f-leading-snug)',
        width: `${Math.max(value.length + 1, 4)}ch`,
        maxWidth: '100%',
        // @ts-expect-error CSS custom property
        '--tw-ring-color': 'var(--f-accent)',
      }}
      aria-label={`Blank ${blank.index + 1} text input`}
    />
  );
}

// ─── MC clickable slot (inline in sentence) ─────────────────────────────────

function MCSlot({
  blank,
  value,
  isDisabled,
  onClear,
}: {
  blank: ClozeBlank;
  value: string;
  isDisabled: boolean;
  onClear: (blankId: string) => void;
}) {
  const hasValue = value.length > 0;

  return (
    <button
      type="button"
      disabled={isDisabled && !hasValue}
      onClick={() => hasValue && onClear(blank.id)}
      className="inline-flex items-center mx-1 px-3 py-0.5 rounded-xl align-baseline transition-all duration-200 min-w-[4ch] cursor-pointer focus:outline-none"
      style={{
        fontSize: 'inherit',
        fontWeight: 'inherit',
        fontFamily: 'var(--f-font-sans)',
        lineHeight: 'var(--f-leading-snug)',
        border: hasValue ? '2px solid var(--f-accent)' : '2px dashed var(--f-border-strong)',
        backgroundColor: hasValue ? 'var(--f-accent)' : 'var(--f-bg-sunken)',
        color: hasValue ? 'var(--f-fg-on-accent)' : 'var(--f-fg-muted)',
      }}
      aria-label={
        hasValue
          ? `Blank ${blank.index + 1}: ${value}. Click to clear.`
          : `Blank ${blank.index + 1}: empty`
      }
    >
      {hasValue ? value : '\u00A0___\u00A0'}
    </button>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function ClozeStep() {
  const { currentStep, state, dispatch } = useSession();
  const { showShortcuts } = useShortcutOverlay();
  const { rippleIndex, rippleKey } = useKeyboardRipple();

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = (snapshot?.config ?? null) as unknown as ClozeConfig | null;
  const blanks = config?.blanks ?? EMPTY_BLANKS;
  const mode = config?.mode ?? 'free_text';
  const isMultipleChoice = mode === 'multiple_choice';

  const answer = state.answer?.type === 'cloze' ? state.answer : null;
  const blanksMap = answer?.blanks ?? EMPTY_BLANKS_MAP;
  const phase = state.phase;
  const result = state.result;
  const blankResults = result?.blankResults;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';

  const blankMap = useMemo(() => new Map(blanks.map((b) => [b.id, b])), [blanks]);

  // Blanks in prompt order (for MC fill logic)
  const blanksInOrder = useMemo(() => {
    const doc = snapshot?.prompt as TiptapNode | undefined;
    if (!doc) return [];
    const ids = extractBlankIds(doc);
    return ids.map((id) => blankMap.get(id)).filter(Boolean) as ClozeBlank[];
  }, [snapshot?.prompt, blankMap]);

  // MC options pool: pre-shuffled by server (correct answers + distractors)
  const allOptions = config?.options ?? EMPTY_OPTIONS;

  // Track which options are used (MC)
  const usedOptionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!isMultipleChoice) return counts;
    for (const b of blanksInOrder) {
      const val = blanksMap[b.id];
      if (val) counts.set(val, (counts.get(val) || 0) + 1);
    }
    return counts;
  }, [isMultipleChoice, blanksInOrder, blanksMap]);

  // Available pool: options minus used
  const availablePool = useMemo(() => {
    if (!isMultipleChoice) return [];
    const remaining = new Map(usedOptionCounts);
    return allOptions.map((opt, i) => {
      const usedCount = remaining.get(opt) || 0;
      if (usedCount > 0) {
        remaining.set(opt, usedCount - 1);
        return { text: opt, index: i, isUsed: true };
      }
      return { text: opt, index: i, isUsed: false };
    });
  }, [isMultipleChoice, allOptions, usedOptionCounts]);

  const hasAnyAnswer = blanksInOrder.some((b) => (blanksMap[b.id] || '').length > 0);
  const [hoveredBubble, setHoveredBubble] = useState<number | null>(null);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleBlankChange = useCallback(
    (blankId: string, value: string) => {
      if (isDisabled || isGraded) return;
      dispatch({
        type: 'UPDATE_ANSWER',
        answer: { type: 'cloze', blanks: { ...blanksMap, [blankId]: value } },
      });
    },
    [isDisabled, isGraded, dispatch, blanksMap]
  );

  // MC: tap a bubble to fill the next empty blank
  const handleBubbleClick = useCallback(
    (optionText: string) => {
      if (isDisabled || isGraded) return;
      for (const b of blanksInOrder) {
        if (!blanksMap[b.id]) {
          handleBlankChange(b.id, optionText);
          return;
        }
      }
    },
    [isDisabled, isGraded, blanksInOrder, blanksMap, handleBlankChange]
  );

  // MC: tap a filled slot to clear it
  const handleSlotClear = useCallback(
    (blankId: string) => {
      if (isDisabled || isGraded) return;
      handleBlankChange(blankId, '');
    },
    [isDisabled, isGraded, handleBlankChange]
  );

  // MC: undo last filled blank
  const handleUndo = useCallback(() => {
    if (isDisabled || isGraded) return;
    for (let i = blanksInOrder.length - 1; i >= 0; i--) {
      if (blanksMap[blanksInOrder[i].id]) {
        handleBlankChange(blanksInOrder[i].id, '');
        return;
      }
    }
  }, [isDisabled, isGraded, blanksInOrder, blanksMap, handleBlankChange]);

  // Reset all blanks
  const handleReset = useCallback(() => {
    if (isDisabled || isGraded) return;
    const cleared: Record<string, string> = {};
    for (const b of blanksInOrder) {
      cleared[b.id] = '';
    }
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'cloze', blanks: cleared } });
  }, [isDisabled, isGraded, blanksInOrder, dispatch]);

  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);

  const diagramJson = (currentStep as unknown as Record<string, unknown>)?.diagramJson as
    | DiagramJson
    | null
    | undefined;

  const promptDoc = snapshot?.prompt as TiptapNode | undefined;

  const firstBlankFocusedRef = useRef(false);
  useEffect(() => {
    firstBlankFocusedRef.current = false;
  }, [currentStep?.id]);

  const renderBlank = useCallback(
    (blankId: string) => {
      const blank = blankMap.get(blankId);
      if (!blank) return null;

      const value = blanksMap[blankId] ?? '';
      const blankResult = blankResults?.[blankId];

      // Graded: show result (shared for both modes)
      if (isGraded && blankResult) {
        return <GradedBlank value={value} blankResult={blankResult} />;
      }

      // MC mode: clickable slot
      if (isMultipleChoice) {
        return (
          <MCSlot blank={blank} value={value} isDisabled={isDisabled} onClear={handleSlotClear} />
        );
      }

      // Free-text mode: expanding input
      const shouldAutoFocus = !firstBlankFocusedRef.current && !isGraded && !isDisabled;
      if (shouldAutoFocus) firstBlankFocusedRef.current = true;

      return (
        <FreeTextBlank
          blank={blank}
          value={value}
          isDisabled={isDisabled || isGraded}
          autoFocus={shouldAutoFocus}
          onChange={handleBlankChange}
        />
      );
    },
    [
      blankMap,
      blanksMap,
      blankResults,
      isGraded,
      isMultipleChoice,
      isDisabled,
      handleSlotClear,
      handleBlankChange,
    ]
  );

  if (!currentStep || !snapshot) return null;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <StepShell>
      {diagramJson?.syntax && (
        <div style={{ padding: 'var(--f-space-6) 0', fontFamily: 'var(--f-font-sans)' }}>
          <DiagramRenderer
            syntax={diagramJson.syntax}
            className="mb-4 rounded-lg overflow-hidden"
          />
        </div>
      )}
      <p
        style={{
          fontSize: 'var(--f-text-sm)',
          color: 'var(--f-fg-muted)',
          marginBottom: 'var(--f-space-2)',
          fontFamily: 'var(--f-font-sans)',
        }}
      >
        Fill in the blanks
      </p>
      {promptDoc && (
        <div style={{ lineHeight: 2, opacity: isDisabled ? 0.7 : 1 }}>
          <PromptRenderer doc={promptDoc} renderBlank={renderBlank} media={mediaItems} />
        </div>
      )}

      {/* MC: bubble option pool */}
      {isMultipleChoice && allOptions.length > 0 && !isGraded && (
        <div style={{ marginTop: 'var(--f-space-6)' }}>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Available options">
            {availablePool.map((opt, i) => (
              <button
                key={opt.index}
                type="button"
                disabled={isDisabled || opt.isUsed}
                onClick={() => handleBubbleClick(opt.text)}
                onMouseEnter={() => !opt.isUsed && !isDisabled && setHoveredBubble(opt.index)}
                onMouseLeave={() => setHoveredBubble(null)}
                className="inline-flex items-center rounded-xl font-medium cursor-pointer active:scale-[0.97] focus:outline-none"
                style={{
                  padding: 'var(--f-space-2) var(--f-space-4)',
                  fontSize: 'var(--f-text-base)',
                  fontFamily: 'var(--f-font-sans)',
                  border: '2px solid',
                  borderColor: opt.isUsed
                    ? 'transparent'
                    : hoveredBubble === opt.index
                      ? 'var(--f-accent)'
                      : 'var(--f-border-strong)',
                  backgroundColor: opt.isUsed ? 'var(--f-bg-sunken)' : 'var(--f-bg-raised)',
                  color: opt.isUsed ? 'var(--f-fg-muted)' : 'var(--f-fg)',
                  opacity: opt.isUsed ? 0.4 : isDisabled ? 0.5 : 1,
                  cursor: opt.isUsed || isDisabled ? 'not-allowed' : 'pointer',
                  transition:
                    'border-color 0.3s ease, background-color 0.3s ease, color 0.3s ease, opacity 0.4s ease',
                  position: 'relative',
                }}
                aria-label={opt.isUsed ? `${opt.text} (used)` : opt.text}
              >
                <RippleFlash active={rippleIndex === opt.index} rippleKey={rippleKey} />
                {opt.text}
                <AnimatedKbd show={!isGraded && !opt.isUsed && showShortcuts} position="inline">
                  {i + 1}
                </AnimatedKbd>
              </button>
            ))}
          </div>

          {/* Undo & Reset */}
          <AnimatePresence>
            {hasAnyAnswer && (
              <motion.div
                className="mt-3 flex items-center gap-2"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={handleUndo}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    fontSize: 'var(--f-text-sm)',
                    color: 'var(--f-fg-secondary)',
                    fontFamily: 'var(--f-font-sans)',
                  }}
                >
                  <Delete size={14} />
                  Undo
                </button>
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={handleReset}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    fontSize: 'var(--f-text-sm)',
                    color: 'var(--f-fg-secondary)',
                    fontFamily: 'var(--f-font-sans)',
                  }}
                >
                  <RotateCcw size={14} />
                  Reset
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* FT: Reset button */}
      {!isMultipleChoice && blanks.length > 0 && !isGraded && (
        <AnimatePresence>
          {hasAnyAnswer && (
            <motion.div
              className="flex items-center gap-2"
              style={{ marginTop: 'var(--f-space-4)' }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <button
                type="button"
                disabled={isDisabled}
                onClick={handleReset}
                className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  fontSize: 'var(--f-text-sm)',
                  color: 'var(--f-fg-secondary)',
                  fontFamily: 'var(--f-font-sans)',
                }}
              >
                <RotateCcw size={14} />
                Reset
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
